/**
 * English interface messages. `{name}` marks a value filled in by `t`. Keys ending in `.one`
 * and `.other` are plural forms picked by `tn`; Arabic may add `.zero`, `.two`, `.few` and
 * `.many`.
 */
export const en = {
  "common.cancel": "Cancel",
  "common.submit": "Submit",
  "common.edit": "Edit",
  "common.close": "Close",
  "ui.sidebar": "Sidebar",
  "ui.sidebarDescription": "Displays the mobile sidebar.",
  "ui.collapseSidebar": "Collapse sidebar",
  "ui.expandSidebar": "Expand sidebar",
  "ui.toggleSidebar": "Toggle sidebar",
  "users.inviteSent": "Login instructions sent by email",
  "common.done": "Done",
  "common.retry": "Retry",
  "common.search": "Search",
  "common.loading": "Loading",
  "common.loadingEllipsis": "Loading...",
  "common.pleaseWait": "Please wait...",
  "common.business": "Business",
  "common.selectBusiness": "Select Business",
  "common.name": "Name",
  "common.email": "Email",
  "common.users": "Users",
  "common.monthly": "Monthly",
  "common.weekly": "Weekly",

  "signOut.title": "Sign out",
  "common.dismiss": "Dismiss",
  "guard.businesses": "Businesses:",
  "guard.licenseTitle": "BI Addon/License Required",
  "guard.licenseBody":
    'Your Business doesn\'t have the necessary "Addon/License" to continue using Foodics BI. Please contact your account manager to extend your free trial or enable the addon.',
  "guard.error": "An error has occurred, try refreshing the page or contact support.",
  "install.needsHelpTitle": "Installation needs help",
  "install.needsHelpBody": "We could not finish preparing this business. Please contact support.",
  "install.checkFailedTitle": "Could not check installation status",
  "install.checkFailedBody": "We will retry the status check automatically.",
  "install.inProgressTitle": "Installation in progress",
  "install.fetching": "Your data is being fetched",
  "install.maxWait": "Maximum wait time: 30 minutes",
  "install.preparing": "Preparing your data",
  "install.lotOfData": "You have a lot of data 🙂",
  "embed.loadFailed": "Failed to load S. Please refresh this page or contact support.",
  "embed.dataContextChanged": "Your data context has changed. Refresh this page to continue.",
  "embed.businessUnavailable":
    "This business is not available yet. Check its installation and license in Settings.",
  "embed.businessSwitchFailed": "Could not change business",
  "signOut.confirm": "Are you sure you want to sign out?",

  "settings.title": "Settings",
  "settings.backToHome": "Back to Home",
  "settings.pages": "Settings pages",
  "settings.page": "Settings page",
  "settings.organization": "Organization",
  "settings.users": "Users",
  "settings.businesses": "Businesses",
  "settings.budgets": "Budgets (Target)",
  "settings.adminsOnly": "Only admins can open Settings.",

  "users.activeDescription": "People with BI accounts",
  "users.inactiveDescription": "Foodics accounts that can receive login instructions",
  "users.accountType": "Account type",
  "users.biAccounts": "BI accounts",
  "users.foodicsAccounts": "Foodics accounts",
  "users.biAccount": "BI account",
  "users.foodicsAccount": "Foodics account",
  "users.pending": "Pending",
  "users.unlicensed": "Unlicensed",
  "users.biRole": "BI role",
  "users.foodicsRoles": "Foodics roles",
  "users.branches": "Branches",
  "users.noRoles": "None",
  "users.owner": "Owner",
  "users.admin": "Admin",
  "users.normal": "Normal",
  "users.normalUser": "Normal User",
  "users.chooseSingleBusiness": "Please choose a single business from the dropdown.",
  "users.noFoodicsAccounts": "No Foodics accounts found",
  "users.noBIAccounts": "No BI accounts found",
  "users.status": "Status",
  "users.role": "Role",
  "users.roles": "Roles",
  "users.invite": "Invite",
  "users.branchCount.one": "{count} Branch",
  "users.branchCount.other": "{count} Branches",
  "users.inviteFailed": "Failed to send invite",
  "users.roleChanged": "User Role was changed Successfully.",
  "users.roleChangeNotPermitted": "Role change operation not permitted",
  "users.roleChangeFailed": "Failed to update user privileges",

  "sync.checking": "Checking refresh…",
  "sync.unavailable": "Refresh status unavailable",
  "sync.refreshingQueued": "Refreshing accounts; your refresh is queued",
  "sync.refreshing": "Refreshing accounts…",
  "sync.yourRefreshQueued": "Your refresh is queued",
  "sync.automaticRetry": "Automatic retry {time}",
  "sync.queued": "Refresh queued",
  "sync.failedAt": "Failed · {time}",
  "sync.lastFailed": "Last refresh failed",
  "sync.lastRun": "Last run {time}",
  "sync.lastRunTitle": "Last run: {time}",
  "sync.available": "Refresh available",
  "sync.anotherStarted": "Another refresh started first. No new refresh was queued.",
  "sync.alreadyQueued": "A refresh was already queued.",
  "sync.availableIn.one": "Refresh is available in {count} min.",
  "sync.availableIn.other": "Refresh is available in {count} min.",
  "sync.didNotStart": "Refresh did not start. Check the status and try again.",
  "sync.availableInMinutes.one": "Refresh available in {count} minute",
  "sync.availableInMinutes.other": "Refresh available in {count} minutes",
  "sync.refreshAccounts": "Refresh accounts",
  "sync.nextRefresh": "Next refresh: {time}",
  "sync.refreshNow": "Refresh accounts now",
  "sync.minutesShort": "{minutes}m",
  "sync.couldNotConfirm": "Could not confirm refresh. Check status and retry.",

  "businesses.addBusiness": "Add Business",
  "businesses.displayName": "Display Name",
  "businesses.businessName": "Business Name",
  "businesses.venues": "Venues",
  "businesses.enterDisplayName": "Enter display name",
  "businesses.renamed": "Successfully Changed Name of Business",
  "businesses.renameFailed": "Failed to rename business",
  "businesses.add.title": "Add a Foodics business",
  "businesses.add.description":
    "Enter a business reference and your email in that Foodics business. New installations require Foodics app-management permission. Moving an installed business requires BI admin access in its current organization.",
  "businesses.add.reference": "Business Reference (F5 Number)",
  "businesses.add.email": "Your Foodics email",
  "businesses.add.code": "Six-digit code",
  "businesses.add.requestNewCodeIn": "Request a new code in {time}",
  "businesses.add.requestNewCode": "Request a new code",
  "businesses.add.tryAgainIn": "Try again in {time}",
  "businesses.add.submit": "Add business",
  "businesses.add.sendCodeIn": "Send code in {time}",
  "businesses.add.sendCode": "Send code",
  "businesses.add.businessNotFound": "We could not find this Foodics business reference.",
  "businesses.add.userNotFound": "This email does not belong to a user of that Foodics business.",
  "businesses.add.codeNotSent": "A code could not be sent. Please try again shortly.",
  "businesses.add.badCode": "Check the code and try again. If it expired, request a new code.",
  "businesses.add.otherAdminMustVerify":
    "An admin of the other business must verify the code to add it here.",
  "businesses.add.codeUsed":
    "This code has been used, or the business is still installing. Request a new code if needed, or try the move after installation finishes.",
  "businesses.add.waitForCountdown": "Please wait until the countdown ends before trying again.",
  "businesses.add.unexpectedVerify":
    "An unexpected error stopped us from adding or moving this business. The code is still usable until it expires; please try again.",
  "businesses.add.unexpectedSend":
    "An unexpected error stopped us from sending a code. Please try again.",
  "businesses.add.unconfirmedVerify":
    "We could not confirm whether the business was added or moved. Refresh your businesses before trying again.",
  "businesses.add.unconfirmedSend":
    "We could not confirm the code request. Check your email before trying again.",
  "businesses.add.installing": "Business added. Installation is in progress.",
  "businesses.add.moved": "Business moved into this organization.",
  "businesses.add.alreadyHere": "Business is already in this organization.",

  "budget.title": "Budget",
  "budget.year": "Year",
  "budget.resolution": "Resolution",
  "budget.noBudget": "Seems like you don't have a budget",
  "budget.noBudgetDemo": "No budget is available in this demo",
  "budget.provideXlsx": "Provide us a proper XLSX to view data",
  "budget.uploadXlsx": "Upload XLSX",
  "budget.uploadTitle": "Upload XLSX file",
  "budget.editTitle": "Edit Budget",
  "budget.templates": "Templates:",
  "budget.uploadedFile": "Uploaded File",
  "budget.deleteFile": "Delete File",
  "budget.uploadYourFile": "Upload your XLSX file",
  "budget.uploadSucceeded": "XLSX Upload Successful",
  "budget.xlsxOnly": "Please upload an XLSX file",
  "budget.unexpectedError": "An unexpected error occurred",
  "budget.invalidFile": "The uploaded file format is invalid",
  "budget.emptyCell": "Empty cell found at row {row}, column {col}",
  "budget.invalidBranchId": 'Invalid branch ID "{branchId}" at row {row}, column {col}',
  "budget.invalidFormat":
    "Invalid format at row {row}, column {col}: expected {expected}, got {got}",
  "budget.invalidValue": "Invalid value at row {row}, column {col}: expected {expected}, got {got}",
  "budget.helpPrefix": "If you are having trouble, read the instructions",
  "budget.helpLink": "here",
  "budget.helpSuffix": ".",
  "budget.branch": "Branch",
  "budget.week": "Week {week}",
  "budget.error": "ERROR",
  "budget.tryRefreshing": "Try refreshing the page",
  "budget.pleaseTryRefreshing": "Please try refreshing the page",
};

export type Messages = typeof en;
export type MessageKey = keyof Messages;
/** The message keys that have plural forms, without their `.one`/`.other` ending. */
export type PluralKey = {
  [K in MessageKey]: K extends `${infer Base}.other` ? Base : never;
}[MessageKey];
